"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";

export default function MentionDropdown({
  anchorRect,
  items,
  onSelect,
  onClose,
  emptyMessage = "No results",
}) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const menuRef = useRef(null);
  const selectedRef = useRef(null);

  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  // Keep the highlighted row visible as the user arrows through the list.
  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  // Scroll the menu ourselves on wheel. The dropdown is portaled to <body>
  // and can sit over the storyboard canvas, whose capture-phase window wheel
  // listener would otherwise swallow the gesture (pan/zoom) before native
  // scrolling runs. A non-passive listener lets us preventDefault + stop it.
  useEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const onWheel = (e) => {
      el.scrollTop += e.deltaY;
      e.preventDefault();
      e.stopPropagation();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [anchorRect]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopImmediatePropagation();
        setSelectedIndex((prev) => (prev + 1) % Math.max(items.length, 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopImmediatePropagation();
        setSelectedIndex(
          (prev) =>
            (prev - 1 + Math.max(items.length, 1)) % Math.max(items.length, 1),
        );
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (items[selectedIndex]) {
          onSelect(items[selectedIndex]);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopImmediatePropagation();
        onClose();
      }
    },
    [items, selectedIndex, onSelect, onClose],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [handleKeyDown]);

  if (!anchorRect) return null;

  const top = anchorRect.bottom + 4;
  const left = anchorRect.left;

  return createPortal(
    <div
      ref={menuRef}
      id="MentionDropdown"
      className="mention-dropdown fixed z-[9999] min-w-[200px] max-w-[320px] max-h-[240px] overflow-y-auto rounded-md border border bg-card shadow-xl"
      style={{ top, left }}>
      {items.length === 0 ? (
        <div className="px-3 py-2 text-xs text-secondary-foreground">
          {emptyMessage}
        </div>
      ) : (
        items.map((item, index) => (
          <button
            id={`MentionDropdown__item-${item.id}`}
            key={item.id}
            ref={index === selectedIndex ? selectedRef : null}
            className={`w-full text-left px-2 py-2 text-sm flex items-center gap-3
                            ${
                              index === selectedIndex
                                ? "bg-primary text-primary-foreground"
                                : "text-secondary-foreground"
                            }`}
            onMouseEnter={() => setSelectedIndex(index)}
            onMouseDown={(e) => {
              e.preventDefault();
              onSelect(item);
            }}>
            {item.icon && (
              <span className="text-base shrink-0">{item.icon}</span>
            )}
            <div className="flex flex-col min-w-0">
              <span className="truncate font-medium text-xs">{item.label}</span>
              {item.description && (
                <span className="text-[10px] opacity-60 truncate">
                  {item.description}
                </span>
              )}
            </div>
          </button>
        ))
      )}
    </div>,
    document.body,
  );
}
